import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { MapPin, Phone, Mail, Linkedin, Globe, Building2 } from 'lucide-react';

/**
 * Client Portal Contact Page
 * Displays Abbey 5 Star contact information including UK and UAE offices
 */
const ClientContactPage = () => {
  // UK Office coordinates: 1 Balloon Street, Manchester, M4 4BE
  const ukCoordinates = { lat: 53.4851, lng: -2.2364 };
  // UAE Office coordinates: Emaar Square, Downtown Dubai
  const uaeCoordinates = { lat: 25.1918, lng: 55.2765 };

  return (
    <div className="space-y-6" data-testid="client-contact-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Contact Us</h1>
        <p className="text-emerald-100">
          Get in touch with Abbey 5 Star for quality, value and reliability in stocktaking and auditing services.
        </p>
      </div>

      {/* Contact Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* UK Office */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5 text-emerald-600" />
              UK Head Office
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
              <address className="not-italic text-slate-600">
                <p className="font-medium text-slate-900">Abbey 5 Star Ltd</p>
                <p>1 Balloon Street</p>
                <p>Manchester, M4 4BE</p>
                <p>United Kingdom</p>
              </address>
            </div>
            
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-slate-400" />
              <a 
                href="tel:+441618284220" 
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                +44 161 828 4220
              </a>
            </div>
            
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-slate-400" />
              <a 
                href="mailto:info@abbey5star.co.uk" 
                className="text-emerald-600 hover:text-emerald-700"
              >
                info@abbey5star.co.uk
              </a>
            </div>

            {/* Map Link */}
            <a 
              href={`https://www.google.com/maps?q=${ukCoordinates.lat},${ukCoordinates.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4"
            >
              <div className="bg-slate-100 rounded-lg p-4 hover:bg-slate-200 transition-colors">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Globe className="h-4 w-4" />
                  <span>View on Google Maps</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Coordinates: {ukCoordinates.lat}, {ukCoordinates.lng}
                </p>
              </div>
            </a>
          </CardContent>
        </Card>

        {/* UAE Office */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5 text-emerald-600" />
              Dubai Office
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
              <address className="not-italic text-slate-600">
                <p className="font-medium text-slate-900">Abbey 5 Star LLC-FZ</p>
                <p>Office 104/105, Level 1</p>
                <p>Emaar Square – Building 4</p>
                <p>Downtown Dubai, UAE</p>
                <p>PO Box: 416654</p>
              </address>
            </div>
            
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-slate-400" />
              <a 
                href="tel:+97144018754" 
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                +971 4 401 8754
              </a>
            </div>
            
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-slate-400" />
              <a 
                href="mailto:info@abbey5star.co.uk" 
                className="text-emerald-600 hover:text-emerald-700"
              >
                info@abbey5star.co.uk
              </a>
            </div>

            {/* Map Link */}
            <a 
              href={`https://www.google.com/maps?q=${uaeCoordinates.lat},${uaeCoordinates.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-4"
            >
              <div className="bg-slate-100 rounded-lg p-4 hover:bg-slate-200 transition-colors">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Globe className="h-4 w-4" />
                  <span>View on Google Maps</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Coordinates: {uaeCoordinates.lat}, {uaeCoordinates.lng}
                </p>
              </div>
            </a>
          </CardContent>
        </Card>
      </div>

      {/* Connect With Us */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Connect With Us</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <a 
              href="https://www.linkedin.com/company/abbey-5-star-stocktaking-services/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white rounded-lg hover:bg-[#005885] transition-colors"
            >
              <Linkedin className="h-5 w-5" />
              <span>Follow us on LinkedIn</span>
            </a>
            
            <a 
              href="https://www.abbey5star.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Globe className="h-5 w-5" />
              <span>Visit our Website</span>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* General Enquiries */}
      <Card className="bg-slate-50">
        <CardContent className="pt-6">
          <div className="text-center">
            <h3 className="font-semibold text-lg text-slate-900 mb-2">General Enquiries</h3>
            <p className="text-slate-600 mb-4">
              For any questions about your audits or our services, please don't hesitate to get in touch.
            </p>
            <a 
              href="mailto:info@abbey5star.co.uk"
              className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
            >
              <Mail className="h-5 w-5" />
              info@abbey5star.co.uk
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientContactPage;
